import type { Maybe, AutopopulateStayZoneOperationFragment, AutopopulateStayZoneOperationsByEventIdQuery } from '@graphql/generated';
import { useAutopopulateStayZoneOperationsByEventIdQuery } from '@graphql/generated';
import { DEFAULT_POLL_SCHEDULE_MS, useAutoPopulationPolling as useBaseAutoPopulationPolling } from '@shared/utils/autoPopulation/useAutoPopulationPolling';
import type { UseAutoPopulationPollingReturn } from '@shared/utils/autoPopulation/useAutoPopulation.types';
import type { AutoPopulationResult, StayZonesContext } from '../stayZonesAutoPopulation.types';

interface UseAutoPopulationPollingOptions
  extends Readonly<{
    eventId?: Maybe<string>;
    shouldSkipAutoPopulationCheck: boolean;
    context: StayZonesContext;
    skipPolling?: boolean;
  }> {}

/**
 * Configuration for extracting and mapping stay zone operations.
 */
const pollingConfig = {
  extractOperations: (data: AutopopulateStayZoneOperationsByEventIdQuery | undefined) => {
    return data?.autopopulateStayZoneOperationsByEventId?.operations;
  },

  mapOperationResults: (operation: AutopopulateStayZoneOperationFragment): ReadonlyArray<AutoPopulationResult> => {
    return operation.result ?? [];
  },

  getOperationStatus: (operation: AutopopulateStayZoneOperationFragment): string => {
    return operation.status;
  },
} as const;

/**
 * Hook to poll auto-populate stay zone operations status.
 * Wraps the base polling hook with stay zones-specific configuration.
 */
export const useStayZonesAutoPopulationPolling = (options: UseAutoPopulationPollingOptions): UseAutoPopulationPollingReturn<AutoPopulationResult> => {
  const { eventId, shouldSkipAutoPopulationCheck, context, skipPolling } = options;

  // Calculate skip condition for the query
  const shouldSkip = !eventId || shouldSkipAutoPopulationCheck || skipPolling;

  // Call query hook unconditionally at top level
  const queryResult = useAutopopulateStayZoneOperationsByEventIdQuery({
    variables: { payload: { eventId: eventId! } },
    skip: shouldSkip,
    batchMode: 'fast',
  });

  return useBaseAutoPopulationPolling<AutopopulateStayZoneOperationsByEventIdQuery, AutopopulateStayZoneOperationFragment, AutoPopulationResult, StayZonesContext>({
    eventId,
    shouldSkipAutoPopulationCheck,
    context,
    queryResult,
    config: pollingConfig,
    pollScheduleMs: DEFAULT_POLL_SCHEDULE_MS,
  });
};
